import { useRef, useState } from 'react'
import { X } from 'lucide-react'
import { Banner, Chip, IconButton, Row, Sheet, useToast } from '../../ui'
import { occurrencesOf, ShowingChips } from '../ShowingChips'
import { useAddItem, useAddNote, useRemoveCommitment, useSaveEntry, useScheduleEntry } from '../../lib/mutations'
import { useMarkSeen } from '../../lib/seen'
import { setItemFields, updateItem } from '../../lib/db'
import { useLists } from '../../lib/queries'
import { supabase } from '../../lib/supabase'
import { hereZone, midnightIsoIn, zoneOfCity } from '../../lib/zone'
import { iso } from '../../lib/calendar'
import { idsOf, signalFeatures } from '../../lib/entries'
import { labelFor, planFor, type AddItemFollowUp } from '../../lib/proposals'
import { go } from '../../lib/router'
import { retryAsk, type CardsById } from '../../lib/chat'
import type { Proposal as ProposalAction } from '../../lib/chatProtocol'
import type { ShowingLite } from '../CardBody'

/**
 * WP 9.7 §2 — `useAddItem`'s own insert takes only `body` and `qty`; a
 * proposal's `link`, `price_cents`, `currency` and `image_url` (what
 * research found — the treadmill flow) never reached the row. One follow-up
 * write with the new row's id, `link` through `updateItem` (`setItemFields`'s
 * own `ListItemFields` does not carry it), everything else through
 * `setItemFields`. Thrown errors surface the same way the insert's own would.
 */
async function applyAddItemFollowUp(id: string, followUp: AddItemFollowUp | undefined): Promise<void> {
  if (!followUp) return
  if (followUp.link !== undefined) {
    const r = await updateItem(id, { link: followUp.link })
    if (r.error) throw new Error(r.error.message)
  }
  const fields: { price_cents?: number; currency?: string; image_url?: string } = {}
  if (followUp.price_cents !== undefined) fields.price_cents = followUp.price_cents
  if (followUp.currency !== undefined) fields.currency = followUp.currency
  if (followUp.image_url !== undefined) fields.image_url = followUp.image_url
  if (Object.keys(fields).length) {
    const r = await setItemFields(id, fields)
    if (r.error) throw new Error(r.error.message)
  }
}

/** How long a press has to hold before it counts as "decline" rather than a
 *  tap — long enough that a tap on the way to a chip's own click never
 *  fires it by accident. */
const HOLD_MS = 550

function errorText(e: unknown): string {
  return e instanceof Error ? e.message : 'Could not do that.'
}

/**
 * One proposal chip — Doug decides. `lib/proposals.ts`'s `planFor` says
 * which mutation a tap runs and with which of the action's own fields;
 * this component supplies what `planFor` cannot know on its own (an
 * event's showings, Doug's lists) and runs it.
 *
 * Three faces, `data-outcome` on the chip itself (`chat.css`): **offered**
 * (the label, tappable), **done** (the label with a check, muted, not
 * tappable), **declined** (struck through, muted). A long-press or the
 * small `×` beside it declines — both 44px, the chip's own tap target.
 *
 * `add_item`'s `Proposal` carries no `entry_id` (`chatProtocol.ts`, frozen
 * by 9.0/9.1) — every add goes through `useAddItem` on a chosen list, never
 * `AddToListSheet`, which needs a whole `FeedRow` to attach an item to.
 */
/** The two proposal kinds that are not a write (WP 9.12). */
export type ChoiceAction = Extract<ProposalAction, { kind: 'ask' | 'followups' }>

export const isChoice = (a: ProposalAction): a is ChoiceAction => a.kind === 'ask' || a.kind === 'followups'

/**
 * WP 9.12 — the butler's question's options (`ask`), or the next questions
 * it offers under an answer (`followups`): a wrapping row of `Chip`s, each
 * sending its own words as Doug's next message. No mutation and no toast —
 * the answer arrives as his bubble, which is the confirmation.
 *
 * The question itself is not drawn here: the butler already said it, in the
 * text above. Follow-ups are quieter than a proposal (`chat.css`: no fill,
 * `--ink-2`), because they are an offer to keep talking, not an action.
 *
 * `Thread` draws this only under the last message, while nothing has been
 * said since — the options belong to the moment the question was asked, so
 * once Doug sends anything they go (and a declined question draws nothing).
 */
export function Choices({ id, action, said, onChoose }: {
  id: string
  action: ChoiceAction
  /** What the butler said in this message, to tell whether it already put
   *  the question in words. */
  said: string
  onChoose: (id: string, text: string) => void
}) {
  const plan = planFor(action)
  if (plan?.hook !== 'send' || plan.texts.length === 0) return null
  const follow = action.kind === 'followups'
  // The brief has the question "already in the text"; 9.11's own `ask`
  // fixture ends on "…or Corpus Delicti at Yorck Kinos." and leaves it
  // implicit. Options with no question over them are a riddle, so the
  // question is drawn in the butler's voice when its words are not there.
  const unsaid = action.kind === 'ask' && !squash(said).includes(squash(action.question))
  return (
    <>
    {unsaid && <p className="chat-say chat-question">{action.question}</p>}
    <div
      className={`chat-chips chat-choices${follow ? ' chat-choices--follow' : ''}`}
      role="group"
      aria-label={labelFor(action, new Map())}
    >
      {plan.texts.map(t => (
        <Chip key={t} className="chat-chip chat-choice" onClick={() => onChoose(id, t)}>
          <span className="chat-chip__text">{t}</span>
        </Chip>
      ))}
    </div>
    </>
  )
}

/** Case, spacing and punctuation aside — "concert, film or either" is the
 *  question "Concert, film, or either?" said. */
const squash = (s: string) => s.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim()

export function Proposal({ id, action, outcome, titles, rows, byEntry, onOutcome }: {
  id: string
  action: ProposalAction
  outcome: 'offered' | 'done' | 'declined'
  /** entry id -> title, for the label and for `schedule`'s "no starts_at". */
  titles: Map<string, string>
  rows: CardsById
  byEntry: Map<string, ShowingLite[]> | undefined
  onOutcome: (id: string, result: 'done' | 'declined') => void
}) {
  const toast = useToast()
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [showingSheet, setShowingSheet] = useState(false)
  const [pickedShowing, setPickedShowing] = useState<ShowingLite | null>(null)
  const [listSheet, setListSheet] = useState(false)

  const saveM = useSaveEntry()
  const scheduleM = useScheduleEntry()
  const removeCommitmentM = useRemoveCommitment()
  const markSeenM = useMarkSeen()
  const addItemM = useAddItem()
  const addNoteM = useAddNote()
  const listsQ = useLists()

  const label = labelFor(action, titles)
  const plan = planFor(action)
  const entryId = 'entry_id' in action ? action.entry_id : null
  const row = entryId ? rows.get(entryId) : undefined
  const now = new Date()

  const offered = outcome === 'offered'
  const disabled = !offered || busy || !plan

  const finish = (result: 'done' | 'declined', toastText?: string) => {
    setError(null)
    onOutcome(id, result)
    if (result === 'done' && toastText) toast({ text: toastText })
  }

  async function run() {
    // `send` is `Choices`' (below), never a chip of this kind; `Thread`
    // routes `ask` and `followups` there.
    if (!plan || plan.hook === 'send') return
    setError(null)
    setBusy(true)
    try {
      switch (plan.hook) {
        case 'save': {
          if (!row) throw new Error('That event is no longer listed.')
          await saveM.mutateAsync({ ids: idsOf(row), features: signalFeatures(row) })
          finish('done', 'Saved')
          break
        }
        case 'schedule': {
          if (!row) throw new Error('That event is no longer listed.')
          let startsAt: string
          if ('needs' in plan) {
            const showings = byEntry?.get(row.id) ?? []
            const upcoming = occurrencesOf(showings)
              .filter(s => new Date(s.starts_at).getTime() >= now.getTime())
              .sort((a, b) => (a.starts_at < b.starts_at ? -1 : 1))
            if (upcoming.length > 1 && !pickedShowing) { setShowingSheet(true); setBusy(false); return }
            const chosen = pickedShowing ?? upcoming[0] ?? null
            startsAt = chosen ? chosen.starts_at : midnightIsoIn(row.run_start ?? iso(now), hereZone())
          } else {
            startsAt = plan.startsAt
          }
          const res = await scheduleM.mutateAsync({
            entryId: row.id, startsAt, title: titles.get(row.id) ?? row.title, features: signalFeatures(row),
          })
          const commitmentId = (res as { data: { id: string } | null }).data?.id ?? null
          finish('done')
          toast({
            text: 'Added to the Year',
            action: commitmentId ? { label: 'Undo', onClick: () => removeCommitmentM.mutate(commitmentId) } : undefined,
          })
          break
        }
        case 'rate': {
          if (!row) throw new Error('That event is no longer listed.')
          await markSeenM.mutateAsync({ entryId: row.id, stars: plan.stars, features: signalFeatures(row) })
          finish('done', plan.stars ? `Rated ${plan.stars} of 5` : 'Marked seen')
          break
        }
        case 'add_item': {
          let listId: string
          if ('needs' in plan) {
            const candidates = (listsQ.data ?? []).filter(l => l.kind === plan.listKind && !l.archived_at)
            if (candidates.length === 1) {
              listId = candidates[0].id
            } else if (candidates.length === 0) {
              throw new Error(`No ${plan.listKind} list yet — add one on Lists first.`)
            } else {
              setListSheet(true)
              setBusy(false)
              return
            }
          } else {
            listId = plan.listId
          }
          const res = await addItemM.mutateAsync({
            listId, body: plan.body,
            opts: { qty: plan.qty ?? null },
          })
          const newId = (res as { data: { id: string } | null }).data?.id ?? null
          if (newId) await applyAddItemFollowUp(newId, plan.followUp)
          finish('done', `Added to list`)
          break
        }
        case 'note': {
          await addNoteM.mutateAsync({ body: plan.body, kind: plan.noteKind, venueId: plan.venueId, entryId: plan.entryId })
          finish('done', 'Note added')
          break
        }
        case 'remember': {
          const res = await supabase.from('chat_memory').insert({ body: plan.body, expires_on: plan.expiresOn ?? null })
          if (res.error) throw new Error(res.error.message)
          finish('done', 'Remembered')
          break
        }
        case 'open': {
          // `city` is a route but not a `Tab` (`router.ts`'s own split —
          // `EXTRA_ROUTES`, not `TABS`), so `go()` — typed to `Tab` — cannot
          // take it; the hash is the same shape either way.
          if (plan.screen === 'city') {
            const qs = plan.query && Object.keys(plan.query).length ? `?${new URLSearchParams(plan.query)}` : ''
            window.location.hash = plan.id ? `/city/${encodeURIComponent(plan.id)}${qs}` : `/city${qs}`
          } else {
            go(plan.screen, plan.id ?? null, plan.query)
          }
          finish('done')
          break
        }
        case 'spend_more': {
          // A row, not an edit to a cost (`spend.ts`'s own reasoning) — the
          // app's own session inserts it, RLS fills `user_id`. Then the
          // question the chip carries, asked again: this chip's own outcome
          // rides along explicitly rather than waiting for whatever Doug
          // types next, the one guarantee this kind gets that no other chip
          // needs (a plain proposal's outcome only ever travels with the
          // *next* message — 9.1's own accepted gap).
          const res = await supabase.from('chat_grants').insert({ amount_usd: plan.amountUsd, day: plan.day })
          if (res.error) throw new Error(res.error.message)
          await retryAsk(plan.retryText, [{ proposal_id: id, result: 'done' }])
          finish('done')
          break
        }
      }
    } catch (e) {
      setError(errorText(e))
    } finally {
      setBusy(false)
    }
  }

  function pickListAndAdd(listId: string) {
    setListSheet(false)
    if (plan?.hook !== 'add_item') return
    setBusy(true)
    const followUp = plan.followUp
    addItemM.mutateAsync({ listId, body: plan.body, opts: { qty: plan.qty ?? null } })
      .then(async res => {
        const newId = (res as { data: { id: string } | null }).data?.id ?? null
        if (newId) await applyAddItemFollowUp(newId, followUp)
        finish('done', 'Added to list')
      })
      .catch((e: unknown) => setError(errorText(e)))
      .finally(() => setBusy(false))
  }

  function pickShowingAndSchedule(s: ShowingLite) {
    setPickedShowing(s)
    setShowingSheet(false)
    if (!row) return
    setBusy(true)
    scheduleM.mutateAsync({ entryId: row.id, startsAt: s.starts_at, title: titles.get(row.id) ?? row.title, features: signalFeatures(row) })
      .then(res => {
        const commitmentId = (res as { data: { id: string } | null }).data?.id ?? null
        finish('done')
        toast({
          text: 'Added to the Year',
          action: commitmentId ? { label: 'Undo', onClick: () => removeCommitmentM.mutate(commitmentId) } : undefined,
        })
      })
      .catch((e: unknown) => setError(errorText(e)))
      .finally(() => setBusy(false))
  }

  // A long-press declines, the same gesture a swipe elsewhere in the app
  // stands in for on a control that is not itself swipeable.
  const holdRef = useRef<number | null>(null)
  const onPointerDown = () => {
    if (!offered) return
    holdRef.current = window.setTimeout(() => finish('declined'), HOLD_MS)
  }
  const clearHold = () => {
    if (holdRef.current != null) { window.clearTimeout(holdRef.current); holdRef.current = null }
  }

  const showings = row ? byEntry?.get(row.id) ?? [] : []
  const upcomingForPicker = occurrencesOf(showings)
    .filter(s => new Date(s.starts_at).getTime() >= now.getTime())
    .sort((a, b) => (a.starts_at < b.starts_at ? -1 : 1))
  const listCandidates = plan?.hook === 'add_item' && 'needs' in plan
    ? (listsQ.data ?? []).filter(l => l.kind === plan.listKind && !l.archived_at)
    : []

  return (
    <span className="chat-propose">
      <Chip
        className="chat-chip"
        data-outcome={outcome}
        disabled={disabled}
        onClick={() => void run()}
        onPointerDown={onPointerDown}
        onPointerUp={clearHold}
        onPointerLeave={clearHold}
        onPointerCancel={clearHold}
      >
        <span className="chat-chip__text">{busy ? 'Working…' : label}</span>
      </Chip>
      {offered && (
        <IconButton
          label={`Decline: ${label}`}
          size="sm"
          className="chat-chip__decline"
          onClick={() => finish('declined')}
        >
          <X aria-hidden="true" />
        </IconButton>
      )}
      {error && <Banner tone="bad" className="chat-chip__error">{error}</Banner>}

      {showingSheet && (
        <Sheet open onOpenChange={o => { if (!o) setShowingSheet(false) }} title="Which showing?">
          <ShowingChips
            showings={upcomingForPicker}
            value={pickedShowing}
            onChange={pickShowingAndSchedule}
            now={now}
            tz={zoneOfCity(row?.city)}
          />
        </Sheet>
      )}

      {listSheet && (
        <Sheet open onOpenChange={o => { if (!o) setListSheet(false) }} title="Add to which list?">
          <div className="ev-acts">
            {listCandidates.map(l => (
              <Row
                key={l.id}
                className="u-row--list"
                leading={null}
                title={l.name}
                meta={null}
                onPress={() => pickListAndAdd(l.id)}
              />
            ))}
          </div>
        </Sheet>
      )}
    </span>
  )
}
